// app/frontend/pages/Auth/ResendConfirmation.tsx
import { useState } from "react";
import { useForm } from "@mantine/form";
import { Stack, Anchor } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconAlertCircle } from "@tabler/icons-react";
import { L2KH1, L2KText } from "../../components/ui/Typography";
import { L2KCard } from "../../components/ui/Cards";
import { L2KInput, L2KButton } from "../../components/ui/Forms";
import AuthFormLayout from "../../components/layouts/AuthFormLayout";
import { SEO } from "../../components/common/SEO";

export default function ResendConfirmation() {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const form = useForm({
    initialValues: {
      email: "",
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setIsLoading(true);
    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;

    try {
      const formData = new FormData();
      formData.append("user[email]", values.email);

      const response = await fetch("/confirmation", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        setEmailSent(true);
        notifications.show({
          color: "green",
          title: "Email Sent",
          message: "Check your inbox for confirmation instructions.",
          icon: <IconCheck />,
          autoClose: 5000,
        });
      } else {
        const data = await response.json().catch(() => ({}));

        notifications.show({
          color: "red",
          title: "Error",
          message: data.error || "Could not send confirmation email. Please try again.",
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });
      }
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "An error occurred. Please try again.",
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <AuthFormLayout>
        <SEO title="Confirmation Email Resent — Learntokiz" />
        <Stack gap="lg">
          <Stack gap="xs" ta="center">
            <L2KH1 variant="accent">Check Your Email</L2KH1>
            <L2KText variant="secondary">
              We've sent confirmation instructions to your email address.
            </L2KText>
          </Stack>

          <L2KCard variant="elevated">
            <Stack gap="md" ta="center">
              <L2KText variant="secondary">
                Click the confirmation link in the email to activate your
                account.
              </L2KText>
              <L2KText variant="tertiary" size="sm">
                Didn't receive the email? Check your spam folder or{" "}
                <Anchor
                  onClick={() => setEmailSent(false)}
                  style={{ color: "#FF9800", cursor: "pointer" }}
                >
                  try again
                </Anchor>
              </L2KText>
              <Anchor href="/login" style={{ color: "#FF9800" }}>
                Back to Sign In
              </Anchor>
            </Stack>
          </L2KCard>
        </Stack>
      </AuthFormLayout>
    );
  }

  return (
    <AuthFormLayout>
      <SEO title="Resend Confirmation - Learntokiz" />
      <Stack gap="lg">
        <Stack gap="xs" ta="center">
          <L2KH1 variant="accent">Resend Confirmation</L2KH1>
          <L2KText variant="secondary">
            Enter your email to receive a new confirmation link.
          </L2KText>
        </Stack>

        <L2KCard variant="elevated" glow>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <L2KInput
                label="Email"
                placeholder="you@example.com"
                required
                {...form.getInputProps("email")}
              />
              <L2KButton
                fullWidth
                mt="md"
                size="md"
                type="submit"
                loading={isLoading}
              >
                Resend Confirmation
              </L2KButton>
            </Stack>
          </form>
        </L2KCard>

        <L2KText size="sm" ta="center">
          <Anchor href="/login" style={{ color: "#FF9800" }}>
            Back to Sign In
          </Anchor>
        </L2KText>
      </Stack>
    </AuthFormLayout>
  );
}
